import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiLayers,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiX,
  FiEye,
  FiAlertTriangle,
  FiTarget,
  FiPercent,
} from "react-icons/fi";
import {
  getSlotsSettings,
  updateSlotsSettings,
  getSlotsHistory,
  fetchAdminGames,
} from "../api/tapGameApi";
import { formatUtcDateTimeWithSeconds } from "../utils/dateUtils";
import GameAnalyticsCharts from "./components/GameAnalyticsCharts";
import GameSessionDetailModal from "./components/GameSessionDetailModal";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return formatUtcDateTimeWithSeconds(dateStr);
  } catch {
    return String(dateStr);
  }
};

export default function SlotsGame() {
  const navigate = useNavigate();

  // Navigation back handler
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Active Tab: "settings" or "history"
  const [activeTab, setActiveTab] = useState("settings");

  // Settings State
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    name: "777 Slots",
    description: "Spin the reels, hit 777 Jackpot and earn massive event points!",
    jackpotPoints: 500,
    tripleBarPoints: 200,
    tripleCherryPoints: 100,
    otherTriplePoints: 50,
    match2Points: 20,
    totalSpins: 5,
    cooldownSeconds: 180,
    isActive: true,
  });

  // Spin Outcome Probability Distribution State
  const [pointProbabilities, setPointProbabilities] = useState({
    jackpotPercentage: 2,
    tripleBarPercentage: 5,
    tripleCherryPercentage: 10,
    otherTriplePercentage: 15,
    match2Percentage: 35,
    lossPercentage: 33,
  });

  const [stats, setStats] = useState({
    totalSessions: 0,
    completedSessions: 0,
    totalPointsAwarded: 0,
  });

  // Daily Analytics Data for Visual Charts
  const [dailyStats, setDailyStats] = useState([]);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);

  // History State
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // UI state
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const [selectedSessionModal, setSelectedSessionModal] = useState(null);
  const searchDebounceTimer = useRef(null);

  // Compute stats helper with serverStats, previous stats retention and pagination fallback
  const computeSlotsStats = (list = [], serverStats = null, prevStats = null, totalFallback = 0) => {
    if (serverStats && (serverStats.totalSessions != null || serverStats.totalSlotsSessions != null)) {
      return {
        totalSessions: Number(serverStats.totalSessions ?? serverStats.totalSlotsSessions ?? 0),
        completedSessions: Number(serverStats.completedSessions ?? serverStats.completedSlotsSessions ?? 0),
        totalPointsAwarded: Number(serverStats.totalPointsAwarded ?? serverStats.totalSlotsPointsAwarded ?? 0),
      };
    }
    // Retain previous real stats if already loaded from getSlotsSettings or fetchAdminGames
    if (prevStats && prevStats.totalSessions > list.length) {
      return {
        totalSessions: totalFallback > prevStats.totalSessions ? totalFallback : prevStats.totalSessions,
        completedSessions: prevStats.completedSessions,
        totalPointsAwarded: prevStats.totalPointsAwarded,
      };
    }
    if (totalFallback > 0) {
      return {
        totalSessions: totalFallback,
        completedSessions: prevStats?.completedSessions || totalFallback,
        totalPointsAwarded: prevStats?.totalPointsAwarded || 0,
      };
    }
    const completedList = list.filter(
      (h) => h.status === "completed" || h.status === "Claimed"
    );
    const totalPts = completedList.reduce(
      (acc, h) =>
        acc +
        Number(h.claimedPoints ?? h.pointsAwarded ?? h.targetPoints ?? h.points ?? 0),
      0
    );
    return {
      totalSessions: list.length,
      completedSessions: completedList.length,
      totalPointsAwarded: totalPts,
    };
  };

  // Fetch Settings
  const fetchSettings = useCallback(async (showToast = false) => {
    setSettingsLoading(true);
    try {
      const res = await getSlotsSettings();
      if (res?.status && res?.data) {
        const d = res.data;
        setFormSettings({
          name: d.name || "777 Slots",
          description: d.description || "Spin the reels, hit 777 Jackpot and earn massive event points!",
          jackpotPoints: Number(d.jackpotPoints ?? 500),
          tripleBarPoints: Number(d.tripleBarPoints ?? 200),
          tripleCherryPoints: Number(d.tripleCherryPoints ?? 100),
          otherTriplePoints: Number(d.otherTriplePoints ?? 50),
          match2Points: Number(d.match2Points ?? 20),
          totalSpins: Number(d.totalSpins ?? 5),
          cooldownSeconds: Number(d.cooldownSeconds ?? 180),
          isActive: Boolean(d.isActive === 1 || d.isActive === true || d.isActive === "1"),
        });

        if (d.pointProbabilities && typeof d.pointProbabilities === "object") {
          setPointProbabilities({
            jackpotPercentage: Number(d.pointProbabilities.jackpotPercentage ?? 2),
            tripleBarPercentage: Number(d.pointProbabilities.tripleBarPercentage ?? 5),
            tripleCherryPercentage: Number(d.pointProbabilities.tripleCherryPercentage ?? 10),
            otherTriplePercentage: Number(d.pointProbabilities.otherTriplePercentage ?? 15),
            match2Percentage: Number(d.pointProbabilities.match2Percentage ?? 35),
            lossPercentage: Number(d.pointProbabilities.lossPercentage ?? 33),
          });
        }

        if (d.stats) {
          setStats({
            totalSessions: Number(d.stats.totalSessions ?? d.stats.totalSlotsSessions ?? 0),
            completedSessions: Number(d.stats.completedSessions ?? d.stats.completedSlotsSessions ?? 0),
            totalPointsAwarded: Number(d.stats.totalPointsAwarded ?? d.stats.totalSlotsPointsAwarded ?? 0),
          });
        }

        if (Array.isArray(d.dailyStats) && d.dailyStats.length > 0) {
          setDailyStats(d.dailyStats);
        }

        // Cross-check with fetchAdminGames if stats are missing or 0
        if (!d.stats || Number(d.stats.totalSessions || 0) === 0) {
          try {
            const gamesRes = await fetchAdminGames();
            const gamesList = gamesRes?.data || gamesRes || [];
            if (Array.isArray(gamesList)) {
              const slotsGame = gamesList.find(
                (g) => g.gameKey === "slots" || g.route === "slots"
              );
              if (slotsGame?.stats) {
                setStats({
                  totalSessions: Number(slotsGame.stats.totalSessions || 0),
                  completedSessions: Number(slotsGame.stats.completedSessions || 0),
                  totalPointsAwarded: Number(slotsGame.stats.totalPointsAwarded || 0),
                });
              }
            }
          } catch {
            // Ignore fallback error
          }
        }
        if (showToast) toast.success("777 Slots settings reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load 777 Slots settings, using defaults:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load 777 Slots settings");
      }
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Fetch History
  const fetchHistory = useCallback(async (showToast = false) => {
    setHistoryLoading(true);
    try {
      const res = await getSlotsHistory({
        page,
        perPage,
        search: debouncedSearch,
        status: statusFilter,
      });

      if (res?.status) {
        const rawHistoryList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
            ? res.data.history
            : [];

        setHistory(rawHistoryList);

        const serverPagination = res.pagination || res.data?.pagination || {};
        const totalRecords =
          typeof serverPagination.total === "number"
            ? serverPagination.total
            : typeof res.total === "number"
              ? res.total
              : rawHistoryList.length;

        const serverStats = res.stats || res.data?.stats;
        setStats((prev) => computeSlotsStats(rawHistoryList, serverStats, prev, totalRecords));

        const totalPagesCount =
          typeof serverPagination.lastPage === "number"
            ? serverPagination.lastPage
            : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalRecords / perPage));

        const currentPageNum =
          typeof serverPagination.currentPage === "number"
            ? serverPagination.currentPage
            : typeof serverPagination.page === "number"
              ? serverPagination.page
              : page;

        setPagination({
          total: totalRecords,
          totalPages: totalPagesCount,
          page: currentPageNum,
          perPage,
        });

        if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
          setDailyStats(res.dailyStats);
        } else if (Array.isArray(res.data?.dailyStats) && res.data.dailyStats.length > 0) {
          setDailyStats(res.data.dailyStats);
        }

        if (showToast) toast.success("777 Slots history reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load 777 Slots history:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load history");
      }
      setHistory([]);
      setStats({ totalSessions: 0, completedSessions: 0, totalPointsAwarded: 0 });
    } finally {
      setHistoryLoading(false);
    }
  }, [page, perPage, debouncedSearch, statusFilter]);

  // Initial Load
  useEffect(() => {
    fetchSettings();
    fetchHistory(false);
    // Fetch wider history sample specifically for multi-day analytics
    getSlotsHistory({ page: 1, perPage: 100 })
      .then((res) => {
        if (res?.status) {
          const list = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.history)
              ? res.data.history
              : [];
          if (list.length > 0) setAnalyticsHistory(list);
          if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
            setDailyStats(res.dailyStats);
          } else if (Array.isArray(res.data?.dailyStats) && res.data.dailyStats.length > 0) {
            setDailyStats(res.data.dailyStats);
          }
        }
      })
      .catch(() => {});
  }, [fetchSettings, fetchHistory]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 350);
  };

  const handleCopySession = (sessionId) => {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    setCopiedSessionId(sessionId);
    toast.success("Session ID copied!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  // Probability helper methods
  const handleSlotsProbChange = (field, value) => {
    setPointProbabilities((prev) => ({
      ...prev,
      [field]: Math.max(0, Math.min(100, Number(value) || 0)),
    }));
  };

  const handleSlotsResetProbabilities = () => {
    setPointProbabilities({
      jackpotPercentage: 2,
      tripleBarPercentage: 5,
      tripleCherryPercentage: 10,
      otherTriplePercentage: 15,
      match2Percentage: 35,
      lossPercentage: 33,
    });
    toast.success("Reset to default 777 Slots probabilities!");
  };

  const handleSlotsAutoBalance = () => {
    const fixedSum =
      Number(pointProbabilities.jackpotPercentage || 0) +
      Number(pointProbabilities.tripleBarPercentage || 0) +
      Number(pointProbabilities.tripleCherryPercentage || 0) +
      Number(pointProbabilities.otherTriplePercentage || 0) +
      Number(pointProbabilities.match2Percentage || 0);
    const remainingForLoss = Math.max(0, 100 - fixedSum);
    setPointProbabilities((prev) => ({
      ...prev,
      lossPercentage: remainingForLoss,
    }));
    toast.success(`Loss percentage balanced to ${remainingForLoss}% to total 100%!`);
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();

    const jackpot = Number(formSettings.jackpotPoints);
    const tripleBar = Number(formSettings.tripleBarPoints);
    const tripleCherry = Number(formSettings.tripleCherryPoints);
    const otherTriple = Number(formSettings.otherTriplePoints);
    const match2 = Number(formSettings.match2Points);
    const spins = Number(formSettings.totalSpins);
    const cDown = Number(formSettings.cooldownSeconds);

    if (isNaN(jackpot) || jackpot <= 0) {
      toast.error("Jackpot points must be greater than 0.");
      return;
    }
    if (isNaN(spins) || spins < 1 || spins > 50) {
      toast.error("Total spins must be between 1 and 50.");
      return;
    }
    if (isNaN(cDown) || cDown < 0 || cDown > 86400) {
      toast.error("Cooldown must be between 0 and 86400 seconds.");
      return;
    }

    // Probability sum validation
    const totalPercentage =
      Number(pointProbabilities.jackpotPercentage || 0) +
      Number(pointProbabilities.tripleBarPercentage || 0) +
      Number(pointProbabilities.tripleCherryPercentage || 0) +
      Number(pointProbabilities.otherTriplePercentage || 0) +
      Number(pointProbabilities.match2Percentage || 0) +
      Number(pointProbabilities.lossPercentage || 0);

    if (totalPercentage !== 100) {
      toast.error(`Total spin probability must equal 100%! Current sum: ${totalPercentage}%`);
      return;
    }

    setSavingSettings(true);
    try {
      const payload = {
        name: formSettings.name,
        description: formSettings.description,
        jackpotPoints: jackpot,
        tripleBarPoints: tripleBar,
        tripleCherryPoints: tripleCherry,
        otherTriplePoints: otherTriple,
        match2Points: match2,
        totalSpins: spins,
        cooldownSeconds: cDown,
        isActive: Boolean(formSettings.isActive),
        pointProbabilities,
      };

      const res = await updateSlotsSettings(payload);
      if (res?.status) {
        toast.success(res.message || "777 Slots settings saved successfully!");
      }
    } catch (err) {
      console.error("Error saving 777 Slots settings:", err);
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen font-sans">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        {/* Full-width container matching Announcement.js layout */}
        <div className="p-5 sm:p-8 space-y-8 w-full">
          {/* Breadcrumb and Back Button */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition flex items-center gap-1">
              <span>Games Management</span>
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-red-700 font-semibold">777 Slots</span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-700 flex items-center justify-center text-2xl shadow-xs">
                🎰
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-black text-gray-900">
                    777 Slot Machine
                  </h1>
                  <span className="bg-red-100 text-red-800 border border-red-300 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                    Live
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure server-driven jackpot rewards, 3-reel point combinations, and inspect spin audit history.
                </p>
              </div>
            </div>

            {/* Top Game Switcher & Refresh Button */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center bg-gray-200/80 p-1 rounded-xl text-xs font-bold">
                <Link
                  to="/games"
                  className="px-2.5 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1"
                  title="All Games Priority & Overview"
                >
                  <FiLayers className="h-3.5 w-3.5" /> All Games
                </Link>
                <Link
                  to="/boom-cards"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🃏</span> Boom Cards
                </Link>
                <Link
                  to="/tap-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎮</span> Tap Game
                </Link>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-white text-black shadow-xs flex items-center gap-1.5"
                >
                  <span>🎰</span> 777 Slots
                </button>
                <Link
                  to="/spin-wheel"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎡</span> Spin Wheel
                </Link>
              </div>

              <button
                onClick={() => {
                  if (activeTab === "settings") fetchSettings(true);
                  else fetchHistory(true);
                }}
                disabled={settingsLoading || historyLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs"
              >
                <FiRefreshCw
                  size={13}
                  className={settingsLoading || historyLoading ? "animate-spin text-red-600" : ""}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-xs border border-gray-200/80 p-5">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                Total Sessions
              </p>
              <p className="text-2xl font-black text-gray-800 font-mono mt-1">
                {stats.totalSessions.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-xs border border-gray-200/80 p-5">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                Completed Sessions
              </p>
              <p className="text-2xl font-black text-emerald-600 font-mono mt-1">
                {stats.completedSessions.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-xs border border-gray-200/80 p-5">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                Total Points Awarded
              </p>
              <p className="text-2xl font-black text-purple-600 font-mono mt-1">
                {stats.totalPointsAwarded.toLocaleString()} <span className="text-xs font-sans text-gray-500 font-medium">PTS</span>
              </p>
            </div>
          </div>

          {/* Daily Users & Daily Points Charts */}
          <GameAnalyticsCharts
            history={analyticsHistory.length > 0 ? analyticsHistory : history}
            dailyStats={dailyStats}
            stats={stats}
            gameName="777 Slots"
            themeColor="red"
          />

          {/* Sub Navigation Tabs */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1.5 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
            >
              <FiSettings size={14} />
              <span>Game Settings & Rewards</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
            >
              <FiList size={14} />
              <span>Spin History & Logs</span>
            </button>
          </div>

          {/* TAB 1: GAME SETTINGS */}
          {activeTab === "settings" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-7">
              {settingsLoading ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">Loading 777 Slots settings...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  {/* Status Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <label className="text-sm font-bold text-gray-800">
                        777 Slot Machine Status
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enable or disable the 777 Slots game in the mobile app.
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold ${formSettings.isActive ? "text-emerald-700" : "text-gray-500"}`}>
                        {formSettings.isActive ? "Active (Enabled)" : "Disabled"}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formSettings.isActive}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, isActive: e.target.checked }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffff00]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Point Combination Rules */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span>🎰</span> Point Combination Payouts
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {/* Jackpot 777 */}
                      <div className="bg-red-50/50 border border-red-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-red-900 mb-1">
                          7️⃣ 7️⃣ 7️⃣ Jackpot Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={50000}
                          value={formSettings.jackpotPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, jackpotPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-red-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-400"
                        />
                        <span className="text-[11px] text-red-700 mt-1 block font-medium">
                          Payout when 3 Sevens align (Default: 500)
                        </span>
                      </div>

                      {/* Triple Bar */}
                      <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-amber-900 mb-1">
                          🍫 🍫 🍫 Triple Bar Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={10000}
                          value={formSettings.tripleBarPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, tripleBarPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-amber-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        <span className="text-[11px] text-amber-700 mt-1 block font-medium">
                          Payout for 3 Bar symbols (Default: 200)
                        </span>
                      </div>

                      {/* Triple Cherry */}
                      <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-purple-900 mb-1">
                          🍒 🍒 🍒 Triple Cherry Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={5000}
                          value={formSettings.tripleCherryPoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, tripleCherryPoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-purple-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <span className="text-[11px] text-purple-700 mt-1 block font-medium">
                          Payout for 3 Cherry symbols (Default: 100)
                        </span>
                      </div>

                      {/* Other Triple Matches */}
                      <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-emerald-900 mb-1">
                          🍋 🍉 Other Triple Match Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={2000}
                          value={formSettings.otherTriplePoints}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, otherTriplePoints: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-emerald-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        />
                        <span className="text-[11px] text-emerald-700 mt-1 block font-medium">
                          Payout for 3 Lemons or Watermelons (Default: 50)
                        </span>
                      </div>

                      {/* Any 2 Matching Symbols */}
                      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4">
                        <label className="block text-xs font-black text-blue-900 mb-1">
                          🍒 🍒 Any 2 Match Points
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          value={formSettings.match2Points}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, match2Points: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-blue-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <span className="text-[11px] text-blue-700 mt-1 block font-medium">
                          Payout when 2 symbols match in a spin (Default: 20)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Spin Outcome Probability Distribution Section */}
                  <div className="bg-red-50/30 border border-red-200/80 rounded-2xl p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-black text-gray-900 flex items-center gap-1.5">
                            <FiTarget className="h-4 w-4 text-red-600" />
                            <span>Spin Win Probability Distribution</span>
                          </h3>
                          {(() => {
                            const sumPct =
                              Number(pointProbabilities.jackpotPercentage || 0) +
                              Number(pointProbabilities.tripleBarPercentage || 0) +
                              Number(pointProbabilities.tripleCherryPercentage || 0) +
                              Number(pointProbabilities.otherTriplePercentage || 0) +
                              Number(pointProbabilities.match2Percentage || 0) +
                              Number(pointProbabilities.lossPercentage || 0);
                            return (
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${sumPct === 100
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                    : "bg-amber-100 text-amber-900 border-amber-300"
                                  }`}
                              >
                                {sumPct === 100 ? (
                                  <>
                                    <FiCheckCircle className="h-3 w-3" />
                                    <span>100% Balanced</span>
                                  </>
                                ) : (
                                  <>
                                    <FiAlertTriangle className="h-3 w-3" />
                                    <span>Sum: {sumPct}% (Requires 100%)</span>
                                  </>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Set the percentage chance of hitting each outcome per spin (Total must sum to 100%).
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSlotsAutoBalance}
                          className="px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer flex items-center gap-1"
                          title="Adjust Loss % to make total sum exactly 100%"
                        >
                          <span>⚡</span> Auto-Balance
                        </button>
                        <button
                          type="button"
                          onClick={handleSlotsResetProbabilities}
                          className="px-2.5 py-1 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer flex items-center gap-1"
                          title="Reset to recommended distribution"
                        >
                          <FiRefreshCw className="h-3 w-3" /> Defaults
                        </button>
                      </div>
                    </div>

                    {/* Proportional Colored Distribution Bar */}
                    <div className="space-y-1.5">
                      <div className="w-full h-3.5 rounded-full bg-gray-200 overflow-hidden flex shadow-inner">
                        {[
                          { key: "jackpotPercentage", color: "bg-red-500", name: "Jackpot 777" },
                          { key: "tripleBarPercentage", color: "bg-amber-500", name: "Triple Bar" },
                          { key: "tripleCherryPercentage", color: "bg-purple-500", name: "Triple Cherry" },
                          { key: "otherTriplePercentage", color: "bg-emerald-500", name: "Other Match 3" },
                          { key: "match2Percentage", color: "bg-blue-500", name: "Match 2" },
                          { key: "lossPercentage", color: "bg-gray-400", name: "Loss / No Match" },
                        ].map((item) => {
                          const pct = Math.max(0, Math.min(100, Number(pointProbabilities[item.key]) || 0));
                          if (pct <= 0) return null;
                          return (
                            <div
                              key={item.key}
                              style={{ width: `${pct}%` }}
                              className={`${item.color} h-full transition-all duration-300 relative group`}
                              title={`${item.name}: ${pct}%`}
                            />
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600 font-medium pt-1">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                          <span>777 Jackpot: <strong>{pointProbabilities.jackpotPercentage}%</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                          <span>Triple Bar: <strong>{pointProbabilities.tripleBarPercentage}%</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                          <span>Triple Cherry: <strong>{pointProbabilities.tripleCherryPercentage}%</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                          <span>Other 3: <strong>{pointProbabilities.otherTriplePercentage}%</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                          <span>Match 2: <strong>{pointProbabilities.match2Percentage}%</strong></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>
                          <span>Loss: <strong>{pointProbabilities.lossPercentage}%</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* 6 Grid Probability Inputs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 pt-1">
                      {/* Jackpot Chance */}
                      <div className="bg-white border border-red-200 border-l-4 border-l-red-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-red-900 mb-1">
                          7️⃣ 7️⃣ 7️⃣ Jackpot Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.jackpotPercentage}
                            onChange={(e) => handleSlotsProbChange("jackpotPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-red-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 2% chance</span>
                      </div>

                      {/* Triple Bar Chance */}
                      <div className="bg-white border border-amber-200 border-l-4 border-l-amber-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-amber-900 mb-1">
                          🍫 🍫 🍫 Triple Bar Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.tripleBarPercentage}
                            onChange={(e) => handleSlotsProbChange("tripleBarPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-amber-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 5% chance</span>
                      </div>

                      {/* Triple Cherry Chance */}
                      <div className="bg-white border border-purple-200 border-l-4 border-l-purple-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-purple-900 mb-1">
                          🍒 🍒 🍒 Triple Cherry Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.tripleCherryPercentage}
                            onChange={(e) => handleSlotsProbChange("tripleCherryPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 10% chance</span>
                      </div>

                      {/* Other Triple Chance */}
                      <div className="bg-white border border-emerald-200 border-l-4 border-l-emerald-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-emerald-900 mb-1">
                          🍋 🍉 Match 3 Fruits Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.otherTriplePercentage}
                            onChange={(e) => handleSlotsProbChange("otherTriplePercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-emerald-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 15% chance</span>
                      </div>

                      {/* Match 2 Chance */}
                      <div className="bg-white border border-blue-200 border-l-4 border-l-blue-500 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-blue-900 mb-1">
                          🍒 🍒 Any 2 Match Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.match2Percentage}
                            onChange={(e) => handleSlotsProbChange("match2Percentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 35% chance</span>
                      </div>

                      {/* Loss / No Match Chance */}
                      <div className="bg-white border border-gray-300 border-l-4 border-l-gray-400 rounded-xl p-3 shadow-2xs">
                        <label className="block text-[11px] font-black text-gray-800 mb-1">
                          ❌ No Match / Loss Chance (%)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pointProbabilities.lossPercentage}
                            onChange={(e) => handleSlotsProbChange("lossPercentage", e.target.value)}
                            className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-black text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 pr-7"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">%</span>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 block">Default: 33% chance</span>
                      </div>
                    </div>
                  </div>

                  {/* Timing and Spin Limits */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span>⏱️</span> Session Rules & Cooldown
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Total Spins Per Round
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={formSettings.totalSpins}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, totalSpins: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        />
                        <span className="text-[11px] text-gray-400 mt-1 block">
                          Default: 5 spins per session
                        </span>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Break / Cooldown Time (Seconds)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={86400}
                          value={formSettings.cooldownSeconds}
                          onChange={(e) =>
                            setFormSettings((prev) => ({ ...prev, cooldownSeconds: e.target.value }))
                          }
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                        />
                        <span className="text-[11px] text-gray-400 mt-1 block">
                          180s = 3 minutes wait time between sessions
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="px-6 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {savingSettings ? (
                        <>
                          <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                          <span>Saving Settings...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="h-3.5 w-3.5" />
                          <span>Save 777 Slots Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 2: AUDIT HISTORY */}
          {activeTab === "history" && (
            <div className="space-y-4">
              {/* Filter Toolbar */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                  <input
                    type="text"
                    placeholder="Search username, session ID, email..."
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:bg-white"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-2 focus:outline-none focus:border-yellow-400"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="started">Started</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              {/* History Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {historyLoading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs font-semibold">Loading 777 Slots history...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="p-12 text-center">
                    <FiInfo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-800 font-bold text-sm">No 777 Slots History Found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[11px]">
                          <th className="py-3.5 px-4">Session ID</th>
                          <th className="py-3.5 px-4">User</th>
                          <th className="py-3.5 px-4 text-center">Spins</th>
                          <th className="py-3.5 px-4 text-right">Claimed Points</th>
                          <th className="py-3.5 px-4 text-center">Status</th>
                          <th className="py-3.5 px-4">Timestamp</th>
                          <th className="py-3.5 px-4 text-center">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((item, idx) => {
                          const sid = item.sessionId || item.session_id || item.id;
                          const shortSid = sid ? `${String(sid).substring(0, 12)}...` : "—";
                          const isCompleted = item.status === "completed" || item.status === "Claimed";
                          const claimedPts = item.claimedPoints ?? item.pointsAwarded ?? item.targetPoints ?? 0;
                          const userId = item.userId || item.user_id || item.user?.id;
                          const userName = item.username || item.userName || item.user?.username || `User #${userId || "—"}`;
                          const spinsCount = Array.isArray(item.spinsData) ? item.spinsData.length : (item.totalSpins || 5);

                          return (
                            <tr
                              key={sid || idx}
                              onClick={() => setSelectedSessionModal(item)}
                              className="hover:bg-red-50/40 transition duration-150 cursor-pointer group"
                              title="Click to view 5 chances points breakdown"
                            >
                              <td className="py-3.5 px-4 whitespace-nowrap font-mono text-gray-700">
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  <span>{shortSid}</span>
                                  {sid && (
                                    <button
                                      onClick={() => handleCopySession(sid)}
                                      className="text-gray-400 hover:text-black p-1 rounded transition cursor-pointer"
                                      title="Copy ID"
                                    >
                                      {copiedSessionId === sid ? (
                                        <FiCheck className="h-3.5 w-3.5 text-emerald-600" />
                                      ) : (
                                        <FiCopy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                {userId ? (
                                  <Link
                                    to={`/user/${userId}`}
                                    className="font-bold text-gray-900 hover:text-red-700 transition"
                                  >
                                    {userName}
                                  </Link>
                                ) : (
                                  <span className="text-gray-800 font-semibold">{userName}</span>
                                )}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span className="px-2 py-0.5 rounded-md bg-gray-100 font-mono font-bold text-gray-700">
                                  {spinsCount} spins
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-right font-mono font-bold text-purple-700">
                                +{Number(claimedPts).toLocaleString()} pts
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isCompleted
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                      : item.status === "started"
                                        ? "bg-blue-50 text-blue-700 border border-blue-300"
                                        : "bg-gray-100 text-gray-600 border border-gray-200"
                                    }`}
                                >
                                  {item.status || "Unknown"}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                                {formatDateTime(item.createdAt || item.startedAt || item.timestamp)}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <button
                                  onClick={() => setSelectedSessionModal(item)}
                                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-gray-700 hover:text-black transition cursor-pointer"
                                  title="View Details"
                                >
                                  <FiEye className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                <div className="p-3.5 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                  <span>Total: {pagination.total} logs</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Prev
                    </button>
                    <span className="font-bold text-gray-800">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session Details Modal with 5 Chances Breakdown */}
      {selectedSessionModal && (
        <GameSessionDetailModal
          session={selectedSessionModal}
          onClose={() => setSelectedSessionModal(null)}
          gameType="slots"
        />
      )}
    </div>
  );
}
